import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useFormik } from 'formik';
import axios from 'axios';
import { showErrorToast, showSuccessToast } from "../utils/toast";
import { Card, Form, Button } from '@themesberg/react-bootstrap';

import { API_URL, END_POINT_QUOTES, END_POINT_PO_SALES } from '../const';
import usePoPaymentTerms from '../hooks/usePoPaymentTerms';
import { clearQuoteDataCache } from '../utils/quoteRequests';
import PoSalesFormFields, { poSalesFormSchema, poSalesPayload } from '../components/PoSalesFormFields';
import {
  buildPoSalesInitialValues,
  getQuoteConversionBlockReason,
  QUOTE_ALREADY_CONVERTED,
} from '../utils/poSales';
import { quoteFormErrors } from '../utils/quoteProducts';

const ConvertPoSalesPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const rawToken = sessionStorage.getItem('quotetoken');
  const token = rawToken?.replace(/^"|"$/g, '');

  const [quote, setQuote] = useState(null);
  const paymentTerms = usePoPaymentTerms();
  const [loading, setLoading] = useState(true);
  const [quoteError, setQuoteError] = useState('');
  const [conflictedQuoteId, setConflictedQuoteId] = useState(null);
  const conversionBlockReason = String(conflictedQuoteId) === String(id)
    ? QUOTE_ALREADY_CONVERTED : getQuoteConversionBlockReason(quote);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setQuote(null);
    setQuoteError('');
    setConflictedQuoteId(null);
    const fetchQuote = async () => {
      try {
        const response = await axios.get(`${API_URL}${END_POINT_QUOTES}/${id}`, {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!active) return;
        if (response.data.status && response.data.data) {
          setQuote(response.data.data);
        } else {
          setQuoteError(response.data.message || 'Could not fetch the selected quote.');
        }
      } catch (error) {
        if (active) setQuoteError(error.response?.data?.message || 'Could not fetch the selected quote.');
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchQuote();
    return () => { active = false; };
  }, [id, token]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: buildPoSalesInitialValues(quote),
    validationSchema: poSalesFormSchema(paymentTerms.data, '', true),
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      if (loading || !quote || conversionBlockReason || paymentTerms.loading || paymentTerms.error || !paymentTerms.data.length) {
        // Never fail silently: tell the user why nothing was sent.
        showErrorToast(paymentTerms.error || (!paymentTerms.loading && !paymentTerms.data.length
          ? 'No payment terms are available. Add a payment term before converting.' : 'The quote is still loading. Please try again.'));
        setSubmitting(false);
        return;
      }
      try {
        const payload = {
          ...poSalesPayload(values),
          po_number: values.po_number.trim(),
          quote_id: values.quote_id || id,
        };

        const response = await axios.post(`${API_URL}${END_POINT_PO_SALES}`, payload, {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        clearQuoteDataCache();
        showSuccessToast(response.data?.message || 'PO Sales created successfully');

        // The listing loads fresh data on arrival, so the new PO appears immediately.
        navigate('/po-sales');
      } catch (error) {
        if (error.response?.status === 409) {
          clearQuoteDataCache();
          setConflictedQuoteId(values.quote_id || id);
          return;
        }
        const errors = error.response?.data?.errors;
        if (errors) setErrors(quoteFormErrors(errors));
        showErrorToast(error.response?.data?.message || 'Could not convert the quote to PO Sales.')
      } finally {
        setSubmitting(false);
      }
    },
  });

  // A failed validation never reaches onSubmit, so point the user at the field that blocked the request.
  useEffect(() => {
    if (formik.submitCount === 0 || formik.isSubmitting || formik.isValid) return;
    showErrorToast('Please correct the highlighted fields before converting.');
    document.querySelector('.is-invalid')?.scrollIntoView?.({ block: 'center' });
  }, [formik.submitCount, formik.isSubmitting]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status" />
          <div className="mt-2">Loading quote details...</div>
        </div>
      </div>
    );
  }

  if (!quote || conversionBlockReason) {
    return (
      <div className="mx-auto py-4" style={{ maxWidth: '1200px' }}>
        <h2 className="page-breadcrumb mb-4">Convert Quote to PO Sales</h2>
        <div role="alert" className="alert alert-warning">
          {conversionBlockReason || quoteError || 'Could not fetch the selected quote.'}
        </div>
        <Link to="/quotes" className="btn btn-secondary">Back to Quotes</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto py-4" style={{ maxWidth: '1200px' }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="page-breadcrumb mb-0">Convert Quote to PO Sales</h2>
        </div>
        <Link to="/quotes">
          <Button variant="secondary">Back to Quotes</Button>
        </Link>
      </div>

      <Card border="light" className="shadow-sm">
        <Card.Body>
          <Form noValidate onSubmit={formik.handleSubmit}>
            <PoSalesFormFields formik={formik} quote={quote} terms={paymentTerms}
              actions={<div className="d-flex justify-content-end gap-2 mt-4">
              <Link to="/quotes">
                <Button variant="outline-secondary">Cancel</Button>
              </Link>
              <Button type="submit" variant="primary" disabled={formik.isSubmitting || paymentTerms.loading || Boolean(paymentTerms.error) || !paymentTerms.data.length}>
                {formik.isSubmitting ? 'Saving...' : 'Convert to PO Sales'}
              </Button>
              </div>} />
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ConvertPoSalesPage;
